// React Query hooks for Wave E drivers-admin pages.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getDriverDevices,
  type DriverDevicesResponse,
  createDriver,
  deleteDriver,
  getDriver,
  listDrivers,
  setDriverStatus,
  updateDriver,
  type ListDriversFilters,
} from '../../../../api/drivers';
import { queryKeys } from '../../../../api/queryKeys';
import { useToast } from '../../../../hooks/useToast';
import type {
  CreateDriverInput,
  DriverListResponse,
  DriverSummary,
  UpdateDriverInput,
} from '../../../../types/api';

export function useDriversList(filters: ListDriversFilters) {
  return useQuery<DriverListResponse>({
    queryKey: queryKeys.driversAdmin.list(filters as unknown as Record<string, unknown>),
    queryFn: () => listDrivers(filters),
    staleTime: 30_000,
  });
}

export function useDriverDevices() {
  return useQuery<DriverDevicesResponse>({
    queryKey: queryKeys.driversAdmin.devices(),
    queryFn: getDriverDevices,
    // Cutover-day screen: keep it fresh without a manual reload.
    refetchInterval: 60_000,
    staleTime: 15_000,
  });
}

export function useDriverDetail(uuid: string | undefined) {
  return useQuery<DriverSummary>({
    queryKey: uuid ? queryKeys.driversAdmin.detail(uuid) : ['drivers-admin', 'detail', 'none'],
    queryFn: () => getDriver(uuid as string),
    enabled: Boolean(uuid),
    staleTime: 30_000,
  });
}

export function useCreateDriver() {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: (input: CreateDriverInput) => createDriver(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.driversAdmin.all });
      void qc.invalidateQueries({ queryKey: queryKeys.drivers.list });
      toast.success('Driver created.');
    },
    onError: () => {
      toast.error('Failed to create driver.');
    },
  });
}

export function useUpdateDriver(uuid: string) {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: (input: UpdateDriverInput) => updateDriver(uuid, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.driversAdmin.detail(uuid) });
      void qc.invalidateQueries({ queryKey: queryKeys.driversAdmin.all });
      void qc.invalidateQueries({ queryKey: queryKeys.drivers.list });
      toast.success('Driver updated.');
    },
    onError: () => {
      toast.error('Failed to update driver.');
    },
  });
}

export function useDeleteDriver() {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: (uuid: string) => deleteDriver(uuid),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.driversAdmin.all });
      void qc.invalidateQueries({ queryKey: queryKeys.drivers.list });
      toast.success('Driver deactivated.');
    },
    onError: () => {
      toast.error('Failed to deactivate driver.');
    },
  });
}

export function useSetDriverStatus() {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: ({
      uuid,
      status,
    }: {
      uuid: string;
      status: 'active' | 'deactivated';
    }) => setDriverStatus(uuid, status),
    onSuccess: (_, vars) => {
      void qc.invalidateQueries({ queryKey: queryKeys.driversAdmin.detail(vars.uuid) });
      void qc.invalidateQueries({ queryKey: queryKeys.driversAdmin.all });
      void qc.invalidateQueries({ queryKey: queryKeys.drivers.list });
      toast.success(
        vars.status === 'active' ? 'Driver reactivated.' : 'Driver deactivated.',
      );
    },
    onError: () => {
      toast.error('Failed to update driver status.');
    },
  });
}
